import { HttpClient, HttpEventType, HttpRequest } from '@angular/common/http';
import { Component, OnInit, ViewChild } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { CoreLibService, FsFile, FsFileUploadRequest } from 'core-lib';
import { MessageService, ToastMessageOptions } from 'primeng/api';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { FileUpload } from 'primeng/fileupload';
import { forkJoin, from, Observable, of } from 'rxjs';
import { map, mergeMap, switchMap, toArray } from 'rxjs/operators';
import { FilestoreService } from '../filestore.service';
import { Message } from 'primeng/message';

@Component({
  selector: 'lib-filestore-upload-web',
  templateUrl: './filestore-upload-web.component.html',
  styleUrl: './filestore-upload-web.component.scss',
  standalone: false
})
export class FilestoreUploadWebComponent implements OnInit {

  files: File[] = [];
  storeId: string;
  parentId: string;
  acceptedFileTypes: string;

  uploadStatus: {
    running: boolean, details: Record<string, {
      error: boolean, complete: boolean, message: string, progress: number
    }>
  } = { running: false, details: {} };

  @ViewChild('fileUpload') fileUpload: FileUpload;


  constructor(private messageService: MessageService, private apollo: Apollo, private fileStoreService: FilestoreService, private dynamicDialogConfig: DynamicDialogConfig, private http: HttpClient, private dialogRef: DynamicDialogRef, public coreLibService: CoreLibService) {

  }


  ngOnInit(): void {
    this.storeId = this.dynamicDialogConfig.data.storeId;
    this.parentId = this.dynamicDialogConfig.data.parentId;
    this.acceptedFileTypes = this.dynamicDialogConfig.data.acceptedFileTypes.join(',');
  }

  onSelect(event): void {
    this.files = event.currentFiles;
    this.files.forEach((file) => {
      this.fileStoreService.fileStoreFileExists(this.storeId, file.name, this.parentId).subscribe((result) => {
        file["exists"] = result;
      })
    });
  }

  doUpload(event): void {
    const files = event.files as File[];

    const fsFileUploadRequests = files.map((file) => {
      const request = {} as FsFileUploadRequest;
      request.contentLength = file.size;
      request.name = file.name;
      request.contentType = file.type;

      return this.fileStoreService.createFile(this.storeId, request, this.parentId).pipe(
        map(mutationResult => ({
          file,
          fsFile: mutationResult.file,
          error: mutationResult.error
        }))
      );
    });

    this.uploadStatus.running = true;

    forkJoin(fsFileUploadRequests).subscribe((results) => {
      const uploadObservables = results.map(({ file, fsFile, error }) => {
        return new Observable<{ fsFile: FsFile, file: File, error?: string }>((ob) => {
          if (error) {
            this.uploadStatus.details[file.name] = {
              progress: 100,
              complete: true,
              message: error,
              error: true
            };
            ob.next({ fsFile, file, error });
            ob.complete();
            return;
          }

          const putUrl = fsFile.uploadUrl;
          const req = new HttpRequest('PUT', putUrl, file, {
            reportProgress: true
          });

          this.uploadStatus.details[file.name] = {
            progress: 0,
            complete: false,
            message: null,
            error: null
          };

          this.http.request(req).subscribe({
            next: (event) => {
              if (event.type === HttpEventType.UploadProgress) {
                const percentDone = Math.round(100 * event.loaded / (event.total ?? 0));
                this.uploadStatus.details[file.name].progress = percentDone;
              }

              if (event.type === HttpEventType.Response) {
                this.uploadStatus.details[file.name].complete = true;
                ob.next({ file, fsFile });
                ob.complete();
              }
            },
            error: (err) => {
              this.uploadStatus.details[file.name] = {
                progress: 100,
                complete: true,
                message: err?.message ?? 'Fehler beim Upload',
                error: true
              };
              ob.next({ fsFile, file, error: err });
              ob.complete();
            }
          });
        });
      });

      // 🟡 Uploads: max. 2 gleichzeitig
      from(uploadObservables).pipe(
        mergeMap(obs => obs, 2),
        toArray()
      ).subscribe((uploadedResults) => {
        const completeObservables = uploadedResults.map(({ fsFile, file, error }) => {
          return of({ fsFile, file, error }).pipe(
            switchMap(({ fsFile, file, error }) => {
              if (error) {
                return of({ file, fsFile, error });
              }
              return this.fileStoreService.fileStoreUploadComplete(this.storeId, fsFile.id).pipe(
                map(result => ({
                  file,
                  fsFile: result.file,
                  error: result.error
                }))
              );
            })
          );
        });

        // 🔵 Abschluss: auch max. 2 gleichzeitig
        from(completeObservables).pipe(
          mergeMap(obs => obs, 2),
          toArray()
        ).subscribe((uploadCompleteResults) => {
          this.uploadStatus.running = false;

          const errors = uploadCompleteResults.filter(r => r.error != null).length;

          if (errors > 0) {
            this.messageService.add({
              severity: 'warn',
              summary: $localize`:@@filestore-upload.successWarn:Upload completed with ${errors} errors`
            } as ToastMessageOptions);

            setTimeout(() => {
              uploadCompleteResults.forEach((result) => {
                if (!result.error) {
                  this.onRemove(result.file);
                }
              });
            }, 1000);
          } else {
            this.messageService.add({
              severity: 'success',
              summary: $localize`:@@filestore-upload.success:Upload successful`
            } as ToastMessageOptions);

            setTimeout(() => {
              this.dialogRef.close({
                changes: uploadCompleteResults.map(r => r.fsFile)
              });
            }, 1000);
          }
        });
      });
    });
  }

  onRemoveAll() {
    this.files.splice(0, this.files.length);
    this.uploadStatus.details = {};
  }

  isImage(file: File): boolean {
    return /^image\//.test(file.type);
  }

  onRemove(file: File) {
    this.files.splice(this.files.indexOf(file), 1);
    delete this.uploadStatus.details[file.name];
  }
}
